import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import ReactImageMagnify from "react-image-magnify";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";
import RotateRightIcon from "@material-ui/icons/RotateRight";
import OpenWithIcon from "@material-ui/icons/OpenWith";
import ImageIcon from "@material-ui/icons/Image";
import ImageGallery from "./image_gallery/ImageGallery";
import { getDayOrNumberSuffix } from "../helpers/getDayOrNumbersuffix";
import { DASHBOARDROUTES } from "../helpers/constants/routes";
import { uploadOutletImages } from "../actions/outlet/uploadOutletImages";
import { saveOutletImages } from "../actions/outlet/saveOutletImages";
import { getShortcutLabel } from "../pages/dashboard/outlets/helper/shortcutUtils";

const ImageZoomWrapper = styled.div`
  display: flex;
  width: ${(props) => props.imageSectionwidth || "570px"};
  max-width: 100%;
  min-height:${(props) => props.noMinHeight ? '0px' : '500px'} ;
  justify-content: space-between;
  margin-right: 0px;
  padding: 10px;
  box-sizing: border-box;

  .magnifier {
    width: 100% !important;
    margin: 10% auto 0;
  }

  @media (max-width: 1200px) {
    width: 100% !important;
    margin-right: 0;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    min-height: 0;
  }
`;

const PreviewSection = styled.div`
  position: relative;
  width: 280px;
  max-width: 100%;
  height: 240px;
  margin-bottom: 30px;
`;

const SingleImageSection = styled.div`
  width: 280px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 12px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  span {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-top: 12px;
    padding: 8px 12px;
    background: #f1f5f9;
    border-radius: 8px;
    width: 100%;
    box-sizing: border-box;

    svg {
      cursor: pointer;
      color: #475569;
      font-size: 20px;
      transition: color 0.2s ease;

      &:hover {
        color: #2563eb;
      }
    }
  }
`;

const GalleryWrapper = styled.div`
  width: ${(props) => (props.imageSectionwidth ? "100%" : "260px")};
  max-width: 100%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    width: 100% !important;
  }
`;

const TotalImageCount = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #ffffff;
  padding: ${props => props.clientScreen ? '8px 16px' : '10px 16px'};
  font-weight: 600;
  font-size: ${props => props.clientScreen ? '13px' : '14px'};
  margin-bottom: 0;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  transition: all 0.2s ease-in-out;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(37, 99, 235, 0.3);
  }
`;

const ImageListWrapper = styled.div`
  margin-bottom: 0;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  padding: 10px;

  button {
    color: #ffffff;
    font-size: 13px;
    font-weight: 600;
    padding: 8px 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #0284c7;
    width: 100%;
    margin: 0 0 10px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #0369a1;
    }
  }

  & > div {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    overflow-y: auto;
    max-height: 140px;
    padding-right: 2px;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 4px;
    }

    div {
      position: relative;
      width: calc(33.333% - 6px);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.06);
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: scale(1.04);
        box-shadow: 0 4px 10px rgba(0,0,0,0.12);
      }

      img {
        width: 100%;
        height: 65px;
        object-fit: cover;
        display: block;
        cursor: pointer;
      }
    }
  }
`;

let initial = true;
function resetOrientation(src, srcOrientation, callback) {
  var img = new Image();
  img.crossOrigin = "anonymous";

  img.onload = function () {
    var width = img.width,
      height = img.height,
      canvas = document.createElement('canvas'),
      ctx = canvas.getContext("2d");

    // set proper canvas dimensions before transform & export
    if (4 < srcOrientation && srcOrientation < 9) {
      canvas.width = height;
      canvas.height = width;
    } else {
      canvas.width = width;
      canvas.height = height;
    }

    // transform context before drawing image
    switch (srcOrientation) {
      case 2: ctx.transform(-1, 0, 0, 1, width, 0); break;
      case 3: ctx.transform(-1, 0, 0, -1, width, height); break;
      case 4: ctx.transform(1, 0, 0, -1, 0, height); break;
      case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
      case 6: ctx.transform(0, 1, -1, 0, height, 0); break;
      case 7: ctx.transform(0, -1, -1, 0, height, width); break;
      case 8: ctx.transform(0, -1, 1, 0, 0, width); break;
      default: break;
    }

    // draw image
    ctx.drawImage(img, 0, 0);

    // Append to the DOM
    // document.getElementById('root').appendChild(canvas);

    // export base64
    callback(canvas.toDataURL());
  };

  if (initial === true) {
    initial = false
    return img.src = src + `?${Math.random()}`
  }
  img.src = src;
}

const ImageListComponent = ({
  title = "Test title",
  images = [
    "https://i.imgur.com/H9utx9q.jpg",
    "https://pyt-images.imgix.net/images/web_app/homepage/banners/2.jpg",
    "https://i.imgur.com/H9utx9q.jpg",
    "https://pyt-images.imgix.net/images/web_app/homepage/banners/2.jpg",
    "https://i.imgur.com/H9utx9q.jpg",
    "https://pyt-images.imgix.net/images/web_app/homepage/banners/2.jpg",
  ],
  onClickAction,
  onClickHandlerForButton
}) => {
  return (
    <ImageListWrapper>
      <button onClick={onClickHandlerForButton}>{title}</button>
      <div>
        {images.map((ele, index) => (
          <div key={index}>
            <img
              onClick={() => onClickAction(ele)}
              alt={"surveyImg"}
              key={index}
              src={ele}
            />
          </div>
        ))}
      </div>
    </ImageListWrapper>
  );
};

const ImageZoom = ({
  showCurrentSurveyImageTiles = true,
  showPrevSurveyImageTiles = false,
  prevAnsImages = {},
  totalImages = [],

  defaultActiveImage = "",
  currentSurveyImages = [],
  imageSectionwidth = "",
  noMinHeight,
  isSkuGroup = false,
  outlet_slug = null,
  outlet_id = null,
  workflowId,
  workflowState = "",
  isShowUploadImage = false,
  workflowSlug,
  prevWorkflowId,
  questionId = null,
  questionAndAnswers = null,
  triggerNotification = null,
  currentQuestionGroupDetails = []
}) => {
  const [activeImage, setActiveImage] = useState("");
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImage, setGallerImage] = useState([]);
  const [noExit, setNoExit] = useState(false);
  // const [uploadedImageIds, setUploadedImageIds] = useState([]);
  const [uploadedImageList, setUploadedImageList] = useState([""]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [hasImageUploadQuestion, setHasImageUploadQuestion] = useState(false);
  const [imageUploadQuestion, setImageUploadQuestion] = useState(null);
  // const [currentImages, setCurrentImages] = useState([]);

  // Find the first image upload question from current question group details
  let targetQuestionId = 0;
  if (!targetQuestionId && currentQuestionGroupDetails && Array.isArray(currentQuestionGroupDetails)) {
    const imageQuestion = currentQuestionGroupDetails.find(question =>
      question.type === 'image_upload'
    );
    if (imageQuestion) {
      targetQuestionId = imageQuestion.id;
    }
  }
  useEffect(() => {
    setActiveImage(defaultActiveImage);
  }, [defaultActiveImage]);

  // useEffect(() => {
  //   setCurrentImages(currentSurveyImages);
  // }, [currentSurveyImages]);

  useEffect(() => {

    if (window.location.search === "?modal=open") {
      setNoExit(true)
      setGallerImage(totalImages);
      setShowGallery(true);
    }
  }, [])

 useEffect(() => {
  if (currentQuestionGroupDetails.length == 0) {
    return
  }
  
  if (!Array.isArray(currentQuestionGroupDetails)) return;

  let hasImageUpload = false;
  let foundQuestion = null;

  for (let i = 0; i < currentQuestionGroupDetails.length; i++) {
    const q = currentQuestionGroupDetails[i];

    if (q.type === "image_upload" && isShowUploadImage) {
      hasImageUpload = true;

      if (targetQuestionId && q.id === targetQuestionId) {
        foundQuestion = q;
      }
    }

    if (hasImageUpload && foundQuestion) break;
  }

  setHasImageUploadQuestion(hasImageUpload);

  if (foundQuestion) {
    setImageUploadQuestion(foundQuestion);
  }

  setUploadedImageList(prev => {
    if (!prev?.length) return prev;
    return [prev[0]];
  });

}, [currentQuestionGroupDetails]);


  const rotateImage = (antiClockWise = false) => {
    let currentOrientation = 1;
    let targetOrientation = currentOrientation;

    if (antiClockWise) {
      switch (currentOrientation) {
        case 1: targetOrientation = 8; break;
        case 8: targetOrientation = 3; break;
        case 3: targetOrientation = 6; break;
        case 6: targetOrientation = 1; break;
        default:
          break;
      }
    } else {
      switch (currentOrientation) {
        case 1: targetOrientation = 6; break;
        case 6: targetOrientation = 3; break;
        case 3: targetOrientation = 8; break;
        case 8: targetOrientation = 1; break;
        default:
          break;
      }
    }

    // setCurrentOrientation(targetOrientation);
    resetOrientation(activeImage, targetOrientation, newImage => setActiveImage(newImage));
  }

  const handleRemoveCurrentSurveyImage = (imageId) => {
    setUploadedImageList(uploadedImageList.filter(item => item.image_id !== imageId));
  };

  const handleFileSelect = (event) => {
    const input = event.target;

    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);

    uploadOutletImages(files)
      .then(response => {
        const newData = response.map(item => ({
          image_id: item.id,
          image_url: item.image
        }));

        const newDataId = response.map(item => item.id);

        setUploadedImageList(prev => [...prev, ...newData])
        setIsUploading(false);
        input.value = null;
      })
      .catch(() => {
        alert('Some images failed to upload');
        setIsUploading(false);
        input.value = null;
      });
  };

  const handleUploadClick = () => {

    if (imageUploadQuestion != null) {
      let alreadyUploadedImageIds = imageUploadQuestion.related_answers?.[0]?.multiple_images_input?.length || 0;
      let totalUploadedImages = uploadedImageList.length - 1  + alreadyUploadedImageIds;


      if (totalUploadedImages >= parseInt(imageUploadQuestion.validations.max_images_count)) {
        triggerNotification({
          messageText: "Maximum image limit exceeded. You can only upload maximum of " + imageUploadQuestion.validations.max_images_count + " images for this question.",
          status: "FAILED",
        });
      } else {
        fileInputRef.current.click();
      }
    } else {
      fileInputRef.current.click();
    }



  };

  const handleSaveClick = () => {
    if (uploadedImageList.length - 1 === 0) {
      if (triggerNotification != null) {
        triggerNotification({
          messageText: "Please upload images first",
          status: "FAILED",
        });
      }

      return;
    }

    if (!outlet_slug) {
      if (triggerNotification != null) {
        triggerNotification({
          messageText: "Missing outlet information",
          status: "FAILED",
        });
      }

      return;
    }

    if (!workflowId) {
      if (triggerNotification != null) {
        triggerNotification({
          messageText: "Missing workflow information",
          status: "FAILED",
        });
      }
      return;
    }



    setIsSaving(true);

    saveOutletImages({
      outlet_slug,
      imageIds: uploadedImageList.filter((item, index) => index > 0).map(item => item.image_id),
      questionId: targetQuestionId,
      workflow_object_id: workflowId
    })
      .then(() => {
        if (triggerNotification != null) {
          triggerNotification({
            messageText: "Images saved successfully!",
            status: "SUCCESS",
          });
        }
        setUploadedImageList([""])
        setIsSaving(false);
      })
      .catch((error) => {
        let errorMessage = 'Unknown error';
        if (error?.detail) {
          errorMessage = error.detail;
        } else if (error?.message) {
          errorMessage = error.message;
        }

        if (triggerNotification != null) {
          triggerNotification({
            messageText: `Failed to save images: ${errorMessage}`,
            status: "FAILED",
          });
        }
        setIsSaving(false);
      });
  };

  return (
    <ImageZoomWrapper noMinHeight={noMinHeight} imageSectionwidth={imageSectionwidth}>
      {(currentSurveyImages.length || showPrevSurveyImageTiles) && (!isSkuGroup && activeImage) ? (
        <SingleImageSection>
          <PreviewSection id="preview" />
          <ReactImageMagnify
            {...{
              smallImage: {
                alt: "Wristwatch by Ted Baker London",
                src: activeImage,
                isFluidWidth: true
              },
              largeImage: {
                src: activeImage,
                width: 1200,
                height: 1200,
              },
              enlargedImageContainerDimensions: {
                width: '100%',
                height: '100%'
              },
              shouldUsePositiveSpaceLens: true,
              lensStyle: {
                width: '64px',
                height: '64px'
              },
            }}
            enlargedImagePortalId={"preview"}
            enlargedImageContainerStyle={{
              height: "240px"
            }}
          />
          <span>
            <RotateLeftIcon onClick={() => rotateImage(true)} />
            <RotateRightIcon onClick={() => rotateImage(false)} />
            <OpenWithIcon
              onClick={() => {
                setGallerImage([activeImage]);
                setShowGallery(true);
              }}
            />
          </span>
        </SingleImageSection>
      ) : null}
      <GalleryWrapper imageSectionwidth={imageSectionwidth}>


        <TotalImageCount
          title={`Total Images (${getShortcutLabel("TOTAL_IMAGE")})`}
          onClick={() => {
            // window.open(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES(outlet_slug, outlet_id, 'total-images'))
            window.open(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES_NEW(outlet_slug, outlet_id, 'total-images'))
            // history.push(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES(outlet_slug, outlet_id))
          }}
          clientScreen={false}
        >
          <ImageIcon style={{
            width: false && "10%"
          }} /> {false && totalImages.length} Total{" "}
          {totalImages.length > 1 ? "Images" : "Image"}
        </TotalImageCount>



        {showCurrentSurveyImageTiles ? (
          <ImageListComponent
            onClickAction={setActiveImage}
            title="Current Survey Image"
            images={currentSurveyImages}
            // onRemoveImage={handleRemoveCurrentSurveyImage}
            onClickHandlerForButton={() => {
              // window.open(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES(outlet_slug, workflowId, `current-survey-images-${workflowSlug}`))
              window.open(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES_NEW(outlet_slug, workflowId, `current-survey-images-${workflowSlug}`))
            }}
          />
        ) : null}
        {showPrevSurveyImageTiles ?
          Object.keys(prevAnsImages).map(key => {
            return <ImageListComponent
              onClickAction={setActiveImage}
              images={prevAnsImages[key]}
              onClickHandlerForButton={() => {
                window.open(DASHBOARDROUTES.GET_DASHBOARD_INDIVIDUAL_OUTLET_VIEW_TOTAL_IMAGES_NEW(outlet_slug, prevWorkflowId, 'prev-survey-images'))
              }}
              title={`${getDayOrNumberSuffix(key)} Prev Survey Images`}
            />
          })
          : null}
        {showGallery ? (
          <ImageGallery
            images={galleryImage.length > 0 ? galleryImage : totalImages}
            setShowGallery={setShowGallery}
            noExit={noExit}
          />
        ) : null}

        {hasImageUploadQuestion && (
          <div style={{
            display: 'flex',
            flexDirection: "column",
            borderTop: "1px solid #f1f5f9",
            paddingTop: "14px"
          }}>
            <h4 style={{ marginTop: 0, marginBottom: 12, fontSize: 14, fontWeight: 600, color: "#1e293b" }}>Upload Image</h4>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 90px)",
                  gap: 12,
                }}
              >
                {uploadedImageList?.map((item, index) => {
                  if (index === 0) {
                    return (
                      <div key={index}>
                        <div
                          onClick={handleUploadClick}
                          style={{
                            width: 90,
                            height: 90,
                            backgroundColor: "#ffffff",
                            border: "2px dashed #94a3b8",
                            borderRadius: 12,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <div
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              backgroundColor: "#3b82f6",
                              color: "#ffffff",
                              fontSize: 24,
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
                            }}
                          >
                            +
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={index}>
                      <div
                        style={{
                          width: 90,
                          height: 90,
                          borderRadius: 12,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                      >
                        <div
                          onClick={() => handleRemoveCurrentSurveyImage(item.image_id)}
                          style={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            background: "#ef4444",
                            color: "white",
                            fontSize: 12,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            fontWeight: "bold",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        >
                          ×
                        </div>

                        <img
                          src={item.image_url}
                          alt=""
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{
              gap: "12px",
              marginTop: "16px",
              justifySelf: "center",
              alignSelf: "center",
            }}>
              <button
                onClick={handleSaveClick}
                disabled={isSaving || uploadedImageList.length - 1 === 0}
                style={{
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: "600",
                  padding: "8px 24px",
                  background: (isSaving || uploadedImageList.length - 1 === 0) ? "#94a3b8" : "#10b981",
                  border: "none",
                  borderRadius: "8px",
                  cursor: (isSaving || uploadedImageList.length - 1 === 0) ? "not-allowed" : "pointer",
                  boxShadow: (isSaving || uploadedImageList.length - 1 === 0) ? "none" : "0 4px 6px -1px rgba(16, 185, 129, 0.3)",
                  transition: "all 0.2s ease"
                }}
              >
                {isSaving ? 'Saving...' : `Save ${uploadedImageList.length - 1 > 0 ? `(${uploadedImageList.length - 1})` : ''}`}
              </button>
            </div>
          </div>
        )}
      </GalleryWrapper>
    </ImageZoomWrapper >
  );
};

export default ImageZoom;